import { FormStateType } from "./Helpers/formTypes";
import { guid } from "./Helpers/guid";
import { LocationDto } from "../../../Api/Model/LocationDto.csharp";
import { formatISO9075 } from "date-fns";

declare var dataLayer: any[] | undefined;

export class GtmService {
  static orderConfirmed(
    formState: FormStateType,
    location: LocationDto | undefined
  ) {
    if (typeof dataLayer != "undefined") {
      const ecommerce = {
        ecommerce: null,
      };

      const data = {
        ecommerce: {
          purchase: {
            actionField: {
              id: guid(),
              affiliation: location?.name ?? formState.location.name,
              revenue: formState.pricingSummary.basePrice,
            },
            products: [
              {
                name: formState.service.name,
                id: "",
                price: formState.pricingSummary.basePrice,
                category: location?.name ?? formState.location.name,
                variant: formState.specializationName,
                quantity: 1,
              },
            ],
          },
        },
      };
      const event = {
        event: "purchase",
      };

      const category = {
        category: location?.name ?? formState.location.name,
        id: "",
        name: formState.service.name,
        price: formState.pricingSummary.basePrice,
        variant: formState.specializationName,
      };

      const specializationSelected = {
        event: "specializationSelected",
        specializationName: formState.specializationName,
      };

      dataLayer.push(ecommerce);
      dataLayer.push(data);
      dataLayer.push(event);
      dataLayer.push(category);
      dataLayer.push(specializationSelected);
    }
  }

  static dropInConfirmed(serviceName: string, centerName: string, id: string) {
    if (typeof dataLayer != "undefined") {
      dataLayer.push({
        event: "WaitingListConfirmed",
        product: serviceName,
        center: centerName,
        uniqueID: id,
      });
    }
  }

  static smsRequested() {
    if (typeof dataLayer != "undefined") {
      dataLayer.push({
        event: "SMSsent",
      });
    }
  }

  static confirmationError(
    service: string,
    specialization: string,
    center: string,
    message: string
  ) {
    if (typeof dataLayer != "undefined") {
      dataLayer.push({
        event: "booking_error",
        error_type: "booking_confirmation_error",
        stage: "step_3",
        info: {
          name: service,
          category: center,
          variant: specialization,
          message: message,
        },
      });
    }
  }

  static bookingCreationError(
    service: string,
    specialization: string,
    center: string,
    message: string
  ) {
    if (typeof dataLayer != "undefined") {
      dataLayer.push({
        event: "booking_error",
        error_type: "booking_creation_error",
        stage: "step_3",
        info: {
          name: service,
          category: center,
          variant: specialization,
          message: message,
        },
      });
    }
  }

  static appointmentsLoadingError(
    service: string,
    specialization: string,
    center: string,
    message: string
  ) {
    if (typeof dataLayer != "undefined") {
      dataLayer.push({
        event: "booking_error",
        error_type: "appointments_loading_error",
        stage: "step_2",
        info: {
          name: service,
          category: center,
          variant: specialization,
          message: message,
        },
      });
    }
  }

  static genericError(message: string) {
    if (typeof dataLayer != "undefined") {
      dataLayer.push({
        event: "booking_error",
        error_type: "unknown_error",
        stage: "unknown",
        info: {
          message: message,
        },
      });
    }
  }
  
  static bookingNoOnlineBooking(
    specializationName: string,
    specializationId: string,
    serviceName: string,
    serviceId: string,
    centerName: string,
    centerId: string,
    message: string
  ) {
    if (typeof dataLayer != "undefined") {
      dataLayer.push({
        event: "booking_error",
        error_type: "No Online Booking",
        stage: "0",
        info: {
          service: specializationName || null,
          service_id: specializationId || null,
          treatment: serviceName || null,
          treatment_id: serviceId || null,
          center: centerName || null,
          center_id: centerId || null,
          message: message || null,
        },
      });
    }
  }
  
  static bookingOverlayLoaded(
    specializationName?: string,
    specializationId?: string,
    serviceName?: string,
    serviceId?: string,
    centerName?: string,
    centerId?: string,
    doctorName?: string,
    doctorId?: string
  ) {
    if (typeof dataLayer != "undefined") {
      dataLayer.push({
        event: "booking_flow",
        ab_test_variant: "new_booking_flow",
        event_name: "Booking Overlay Opened",
        booking: {
          desc: "booking_overlay_opened",
          tracking_code: "a1",
          info: {
            step: 0,
            service: specializationName || null,
            service_id: specializationId || null,
            treatment: serviceName || null,
            treatment_id: serviceId || null,
            center: centerName || null,
            center_id: centerId || null,
            treatment_provider: doctorName || null,
            doctor_id: doctorId || null,
          },
        },
      });
    }
  }

  static appointmentStepSubmit(
    specializationName: string,
    specializationId: string,
    serviceName: string,
    serviceId: string,
    centerName: string,
    centerId: string,
    doctorName: string,
    doctorId: string,
    appointmentDate: Date,
    breadcrumbsShown?: boolean
  ) {
    if (typeof dataLayer != "undefined") {
      dataLayer.push({
        event: "booking_flow",
        ab_test_variant: "new_booking_flow",
        event_name: "Appointment Selected",
        booking: {
          desc: "booking_step_two",
          tracking_code: "a3",
          info: {
            step: 2,
            service: specializationName || null,
            service_id: specializationId || null,
            treatment: serviceName || null,
            treatment_id: serviceId || null,
            center: centerName || null,
            center_id: centerId || null,
            treatment_provider: doctorName || null,
            doctor_id: doctorId || null,
            appointment_date: GtmService.formatAppointmentDate(appointmentDate),
            appointment_date_unix_time: appointmentDate.getTime(),
            breadcrumb_shown: breadcrumbsShown
          },
        },
      });
    }
  }

  static personalInfoStepSubmit(
    specializationName: string,
    specializationId: string,
    serviceName: string,
    serviceId: string,
    centerName: string,
    centerId: string,
    doctorName: string,
    doctorId: string,
    appointmentDate: Date,
    newsletterSignIn: boolean,
    price: number,
    breadcrumbsShown?: boolean
  ) {
    if (typeof dataLayer != "undefined") {
      dataLayer.push({
        event: "booking_flow",
        ab_test_variant: "new_booking_flow",
        event_name: "Contact Information Submitted",
        booking: {
          desc: "booking_step_three",
          tracking_code: "a4",
          info: {
            step: 3,
            service: specializationName || null,
            service_id: specializationId || null,
            treatment: serviceName || null,
            treatment_id: serviceId || null,
            center: centerName || null,
            center_id: centerId || null,
            treatment_provider: doctorName || null,
            doctor_id: doctorId || null,
            appointment_date: GtmService.formatAppointmentDate(appointmentDate),
            appointment_date_unix_time: appointmentDate.getTime(),
            sms_reminder: true,
            newsletterSignIn: newsletterSignIn,
            price: price,
            breadcrumb_shown: breadcrumbsShown
          },
        },
      });
    }
  }

  static appointmentConfirmed(
    specializationName: string,
    specializationId: string,
    serviceName: string,
    serviceId: string,
    centerName: string,
    centerId: string,
    doctorName: string,
    doctorId: string,
    appointmentDate: Date,
    newsletterSignIn: boolean,
    price: number,
    breadcrumbsShown?: boolean
  ) {
    if (typeof dataLayer != "undefined") {
      dataLayer.push({
        event: "booking_flow",
        ab_test_variant: "new_booking_flow",
        event_name: "Booking Confirmed",
        booking: {
          desc: "booking_confirmed",
          tracking_code: "a5",
          info: {
            step: 4,
            service: specializationName || null,
            service_id: specializationId || null,
            treatment: serviceName || null,
            treatment_id: serviceId || null,
            center: centerName || null,
            center_id: centerId || null,
            treatment_provider: doctorName || null,
            doctor_id: doctorId || null,
            appointment_date: GtmService.formatAppointmentDate(appointmentDate),
            appointment_date_unix_time: appointmentDate.getTime(),
            sms_reminder: true,
            newsletter_sign_up: newsletterSignIn,
            price: price,
            appointment_id: guid(),
            breadcrumb_shown: breadcrumbsShown
          },
        },
      });
    }
  }

  static bookingAppointmentsScreenShown(
    specializationName: string,
    specializationId: string,
    serviceName: string,
    serviceId: string,
    centerName: string,
    centerId: string,
    doctorName?: string,
    doctorId?: string,
    isBookingFallback?: boolean,
    breadcrumbsShown?: boolean
  ) {
    if (typeof dataLayer != "undefined") {
      dataLayer.push({
        event: "booking_flow",
        ab_test_variant: "new_booking_flow",
        event_name: "Appointments Screen Shown",
        booking: {
          desc: "appointments_shown",
          tracking_code: "a9",
          info: {
            service: specializationName || null,
            service_id: specializationId || null,
            treatment: serviceName || null,
            treatment_id: serviceId || null,
            center: centerName || null,
            center_id: centerId || null,
            treatment_provider: doctorName || null,
            doctor_id: doctorId || null,
            booking_fallback: isBookingFallback || false,
            breadcrumb_shown: breadcrumbsShown
          },
        },
      });
    }
  }

  static initialStepSubmit(
    specializationName: string,
    specializationId: string,
    serviceName: string,
    serviceId: string,
    centerName: string,
    centerId: string,
    doctorName?: string,
    doctorId?: string,
  ) {
    if (typeof dataLayer != "undefined") {
      dataLayer.push({
        event: "booking_flow",
        ab_test_variant: "new_booking_flow",
        event_name: "Initial Step Submitted",
        booking: {
          desc: "initial_step_submitted",
          tracking_code: "a6",
          info: {
            step: 0,
            service: specializationName || null,
            service_id: specializationId || null,
            treatment: serviceName || null,
            treatment_id: serviceId || null,
            center: centerName || null,
            center_id: centerId || null,
            treatment_provider: doctorName || null,
            doctor_id: doctorId || null,
          },
        },
      });
    }
  }

  static treatmentStepSubmit(
    specializationName: string,
    specializationId: string,
    serviceName: string,
    serviceId: string,
    centerName: string,
    centerId: string,
    doctorName?: string,
    doctorId?: string,
  ) {
    if (typeof dataLayer != "undefined") {
      dataLayer.push({
        event: "booking_flow",
        ab_test_variant: "new_booking_flow",
        event_name: "Treatment Step Submitted",
        booking: {
          desc: "treatment_step_submitted",
          tracking_code: "a7",
          info: {
            step: 0,
            service: specializationName || null,
            service_id: specializationId || null,
            treatment: serviceName || null,
            treatment_id: serviceId || null,
            center: centerName || null,
            center_id: centerId || null,
            treatment_provider: doctorName || null,
            doctor_id: doctorId || null,
          },
        },
      });
    }
  }

  private static formatAppointmentDate(appointmentDate: Date) {
    return formatISO9075(appointmentDate);
  }
}
